import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useGlobalContext } from "../Config";
import { Dropdown } from "primereact/dropdown";
import { useState } from "react";
import { useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { useMemo } from "react";


function FormEditarDocumento({
    isFormVisible,
    setIsFormVisible,
    toggleFormVisibility,
    idDocumento,
    idEstadoDocumento,  
    fileName,
    variablesJson,
    metadataJson,
    fetchDocumentos,
    toast,
}) {
    const [loading, setLoading] = useState(false);
    const { urlContratos } = useGlobalContext();
    const [estadosDocumentoOptions, setEstadosDocumentoOptions] = useState([]);
    const [estadoSeleccionado, setEstadoSeleccionado] = useState(null);
    const [filename, setFilename] = useState('');
    const [formValues, setFormValues] = useState({});

    // Obtiene las claves de variables desde variablesJson (string | array | objeto)
    const variablesKeys = useMemo(() => {
        try {
            const raw = variablesJson;
            const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

            let keys = [];
            if (Array.isArray(parsed)) {
                keys = parsed
                    .map(v => (typeof v === 'string' ? v : v?.name || v?.key || v?.campo || ''))
                    .filter(Boolean);
            } else if (parsed && typeof parsed === 'object') {
                keys = Object.keys(parsed);
            }

            return keys;
        } catch {
            return [];
        }
    }, [variablesJson]);

    useEffect(() => {
        if (isFormVisible) {
            obtenerEstadosDocumento();

            setEstadoSeleccionado(idEstadoDocumento ?? null);
            setFilename(fileName || '');

            try {
                const metaObj = typeof metadataJson === 'string'
                    ? JSON.parse(metadataJson || '{}')
                    : (metadataJson || {});

                // Prellenado: usa variablesKeys; si vienen vacías, cae a claves de metaObj
                const keys = variablesKeys.length ? variablesKeys : Object.keys(metaObj || {});
                const merged = {};
                keys.forEach((k) => {
                    merged[k] = metaObj[k] ?? '';
                });
                setFormValues(merged);
            } catch {
                setFormValues({});
            }
        } else {
            setFormValues({});
            setFilename('');
            setEstadoSeleccionado(null);
        }
    }, [isFormVisible, variablesKeys, idEstadoDocumento, fileName, metadataJson]);

    const obtenerEstadosDocumento = async () => {
        try {
            const response = await fetch(
                `${urlContratos}getAllEstadosDocumento`,
                {
                    method: 'GET',
                    credentials: 'include',
                }
            );
            const data = await response.json();
            const lista = Array.isArray(data.estadosDocumentos) ? data.estadosDocumentos : Object.values(data.estadosDocumentos || {});
            setEstadosDocumentoOptions(lista);
        } catch (error) {
            console.error("Error al obtener los estados de documento:", error);
        }
    };

    const handleGuardar = async () => {
        try {
            setLoading(true);  
            const normalizeMetadata = (values) =>
                Object.fromEntries(
                    Object.entries(values || {}).map(([k, v]) => {
                        if (v instanceof Date) {
                            const y = v.getFullYear();
                            const m = String(v.getMonth() + 1).padStart(2, '0');
                            const d = String(v.getDate()).padStart(2, '0');
                            return [k, `${y}-${m}-${d}`];
                        }
                        return [k, v];
                    })
                );

            const payload = {
                id: idDocumento,
                idEstadoDocumento: estadoSeleccionado ?? idEstadoDocumento ?? null,
                idUsuario: Number(sessionStorage.getItem('usuario_id') || 0),
                filename: filename,
                metadataJson: normalizeMetadata(formValues),
            };

            const response = await fetch(`${urlContratos}versionarDocumento`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
                credentials: 'include',
            });

            if (!response.ok) {
                throw new Error(`Error al actualizar documento (${response.status})`);
            }
            
            await fetchDocumentos();
            toast.current?.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Documento actualizado con éxito',
                life: 3000
            });
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);  
            setIsFormVisible(false);
        }
    };

    // Variables derivadas de las claves de formValues (prellenadas desde metadataJson)
    const variables = useMemo(() => Object.keys(formValues || {}), [formValues]);

    const resetFormDocumento = () => {
        setEstadoSeleccionado(null);
        setFormValues({});
    };

    const handleDialogHide = () => {
        resetFormDocumento();
        setIsFormVisible(false);
    };

    return (
        <div>
            <Dialog
                visible={isFormVisible}
                onHide={toggleFormVisibility}
                className="p-fluid product-dialog"
                modal
                footer={
                    <div className="flex justify-content-end">
                        <Button
                            label="Cancelar"
                            icon="pi pi-times"
                            className="p-button mr-2 p-button-rounded p-button-outlined"
                            onClick={handleDialogHide}
                            disabled={loading}
                        />
                        <Button
                            label="Guardar"
                            icon={loading ? "pi pi-spin pi-spinner" : "pi pi-save"}
                            className="p-button-success p-button-rounded p-button"
                            raised
                            onClick={handleGuardar}
                            disabled={loading}
                        />
                    </div>
                }
                header="Editar Documento"
            >

                <div className="card">
                    <h6 className="mb-3">
                        Información del Documento
                    </h6>                
              
                    <span className="p-float-label w-full mt-3">
                        <InputText
                            id="filename"
                            value={filename}
                            onChange={(e) => setFilename(e.target.value)}
                            placeholder="Ingrese nombre del archivo"
                            maxLength={100}
                            className="w-full p-inputtext-lg"
                            required
                            disabled={loading}
                        />
                        <label htmlFor="filename">Nombre de Documento</label>
                    </span>

                    <span className="p-float-label w-full mt-3">
                        <Dropdown
                            value={estadoSeleccionado}
                            onChange={(e) => setEstadoSeleccionado(e.value)}
                            options={estadosDocumentoOptions}
                            optionLabel="descripcion"
                            optionValue="id"
                            placeholder="Seleccione un estado"
                            className="w-full p-inputtext-lg"
                            required
                            disabled={loading}
                        />
                        <label className="mb-1">Estado</label>
                    </span>
                
                    {/* renderiza un input por cada variable */}
                    <h6 className="mb-3">
                        Datos del Documento
                    </h6>   
                    <div className="grid mt-3">
                        
                        {variablesKeys.length === 0 ? (
                            <div className="col-12 text-500">No hay datos disponibles para este documento.</div>
                        ) : (
                            variablesKeys.map((key) => (
                                <span className="p-float-label w-full mt-3">
                                    <InputText
                                        id={key}
                                        value={formValues[key] ?? ''}
                                        onChange={(e) => setFormValues({ ...formValues, [key]: e.target.value })}
                                        placeholder={key}
                                        className="w-full p-inputtext-lg"
                                        required
                                        disabled={loading}
                                    />
                                    <label htmlFor={key}>{key}</label>
                                </span>
                            ))
                        )}
                    </div>
                </div>
            </Dialog>
        </div>
    );
}

export default FormEditarDocumento;
